import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, Input, Typography, MailOutlined } from '@/shared/antd-imports';
import clientPortalAPI from '@/services/api';

interface MagicLinkRequestFormProps {
  /** Prefill (the email typed in the password form). */
  initialEmail?: string;
  onUsePassword: () => void;
}

interface MagicLinkFormValues {
  email: string;
}

/** HTTP status of an axios error, if any. */
const statusOf = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : null;
};

/**
 * ANS-55: "Email me a sign-in link". The answer never says whether the account exists; the link works once and
 * expires after 15 minutes. Errors: invalid email (400), too many requests (429), network or server error.
 */
export const MagicLinkRequestForm: React.FC<MagicLinkRequestFormProps> = ({ initialEmail, onUsePassword }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<MagicLinkFormValues>();
  const [isSending, setIsSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async ({ email }: MagicLinkFormValues) => {
    setIsSending(true);
    setError(null);
    try {
      const response = await clientPortalAPI.requestMagicLink(email.trim());
      if (response.done) setSentTo(email.trim());
      else setError(t('login.magic.failed', { defaultValue: 'The link could not be sent. Please try again.' }));
    } catch (err) {
      const status = statusOf(err);
      setError(
        status === 429
          ? t('login.magic.tooMany', { defaultValue: 'Too many sign-in link requests. Please try again later.' })
          : status === 400
            ? t('login.emailInvalid', { defaultValue: 'Please enter a valid email address' })
            : t('login.magic.failed', { defaultValue: 'The link could not be sent. Please try again.' })
      );
    } finally {
      setIsSending(false);
    }
  };

  if (sentTo) {
    return (
      <div>
        <Alert
          type="success"
          showIcon
          role="status"
          message={t('login.magic.sentTitle', { defaultValue: 'Check your email' })}
          description={t('login.magic.sent', {
            defaultValue:
              'If an account with {{email}} can sign in, we sent a sign-in link. It works once and expires in 15 minutes.',
            email: sentTo,
          })}
          style={{ marginBottom: 16 }}
        />
        <Button block onClick={() => setSentTo(null)} style={{ marginBottom: 8 }}>
          {t('login.magic.sendAgain', { defaultValue: 'Send another link' })}
        </Button>
        <Button block type="link" onClick={onUsePassword}>
          {t('login.magic.usePassword', { defaultValue: 'Sign in with a password instead' })}
        </Button>
      </div>
    );
  }

  return (
    <Form
      form={form}
      name="magic-link"
      layout="vertical"
      requiredMark={false}
      initialValues={{ email: initialEmail ?? '' }}
      onFinish={values => void onFinish(values)}
    >
      <Typography.Paragraph type="secondary">
        {t('login.magic.help', {
          defaultValue: 'We email you a link that signs you in without a password. It works once and expires in 15 minutes.',
        })}
      </Typography.Paragraph>
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />
      )}
      <Form.Item
        name="email"
        label={t('login.emailLabel', { defaultValue: 'Email Address' })}
        rules={[
          { required: true, message: t('login.emailRequired', { defaultValue: 'Please enter your email address' }) },
          { type: 'email', message: t('login.emailInvalid', { defaultValue: 'Please enter a valid email address' }) },
        ]}
      >
        <Input prefix={<MailOutlined />} placeholder={t('login.email', { defaultValue: 'Email' })} autoComplete="email" inputMode="email" />
      </Form.Item>
      <Form.Item style={{ marginBottom: 8 }}>
        <Button block type="primary" htmlType="submit" loading={isSending}>
          {t('login.magic.send', { defaultValue: 'Email me a sign-in link' })}
        </Button>
      </Form.Item>
      <Button block type="link" onClick={onUsePassword}>
        {t('login.magic.usePassword', { defaultValue: 'Sign in with a password instead' })}
      </Button>
    </Form>
  );
};

export default MagicLinkRequestForm;
